import { NumericFieldPolicy } from './numeric-policy.util';

/**
 * Pure value-coercion helpers for numeric filing inputs.
 *
 * Extracted verbatim from four directives that each reimplemented the same
 * PrimeNG/native branching around this logic. Keeping them as plain string
 * functions means they are unit-testable without a TestBed, and lets the
 * single `appSchemaNumeric` dispatcher own the lifecycle once instead of
 * four times.
 *
 * All helpers take and return the *string* form of the field, because that
 * is what a keystroke and a paste both produce; numeric conversion happens
 * once, at the directive boundary.
 */

/**
 * Expand JavaScript's exponential number notation into a plain fixed-point
 * decimal string, digit-for-digit, with no rounding.
 *
 * `String(value)` renders in exponential form at both ends of the range —
 * `|x| >= 1e21` and `0 < |x| < 1e-7` — most commonly as floating-point
 * residue from a recomputed total (`0.1 + 0.2 - 0.3 === 5.551115123125783e-17`).
 * Left alone, `clampDecimals`/`coerceInteger` treat the `e`/`+`/`-` of the
 * exponent as noise and strip them, turning `1e-7` into the digit string
 * `"17"` — a silent, order-of-magnitude corruption of a value a filing sheet
 * is about to submit.
 *
 * This only *repositions* the decimal point across the mantissa's existing
 * digits (padding with zeros where the exponent shifts past them); it never
 * invents a digit or drops one, so it is lossless and introduces no rounding
 * of its own. `clampDecimals`/`coerceInteger` truncate the result exactly as
 * they already do for any other plain decimal string.
 *
 * Not exponential notation (no match) -> returned unchanged.
 */
function expandExponential(input: string): string {
  const match = /^(-)?(\d+)(?:\.(\d+))?[eE]([+-]?\d+)$/.exec(input);
  if (!match)
    return input;

  const [, sign, intPart, fracPart = '', expStr] = match;
  const digits = intPart + fracPart;
  const pointPos = intPart.length + parseInt(expStr, 10);

  let unsigned: string;
  if (pointPos <= 0)
    unsigned = '0.' + '0'.repeat(-pointPos) + digits;
  else if (pointPos >= digits.length)
    unsigned = digits + '0'.repeat(pointPos - digits.length);
  else
    unsigned = digits.slice(0, pointPos) + '.' + digits.slice(pointPos);

  return (sign ?? '') + unsigned;
}

/** Keep at most `maxDecimals` fraction digits; normalise sign, dots, zeros. */
export function clampDecimals(input: string, maxDecimals: number): string {
  let value = expandExponential(input).replace(/[^0-9.-]/g, '');

  if (value.indexOf('-') > 0)
    value = value.replace(/-/g, '');
  if (value.startsWith('-'))
    value = '-' + value.slice(1).replace(/-/g, '');

  const parts = value.split('.');
  if (parts.length > 2)
    value = parts[0] + '.' + parts.slice(1).join('');

  if (value.includes('.')) {
    const [intPart, decimalPart] = value.split('.');
    value = intPart + '.' + (decimalPart.slice(0, maxDecimals) ?? '');
  }

  return value.replace(/^(-?)0+(?=\d)/, '$1');
}

/**
 * Cap an over-length value to the field's digit budget, keeping the digits
 * READ LEFT-TO-RIGHT and dropping the right-most ones.
 *
 * ## The rule (PO, 2026-08-17)
 *
 * When the integer part overflows, the value is treated as ONE digit stream —
 * integer digits followed by fraction digits, the decimal point discarded — and
 * rebuilt from the left:
 *
 * - integer part = the first `maxIntegerDigits` digits of the stream;
 * - fraction     = the next `decimals` digits (shorter, or absent, if the
 *   stream runs out);
 * - everything further right is dropped. The sign is preserved.
 *
 * So on a 13+2 monetary field `1234567890123456789` becomes `1234567890123.45`
 * — the PO's worked example. On a 0-decimal field the fraction step contributes
 * nothing, leaving a plain "first `maxIntegerDigits` digits".
 *
 * A value at or under the cap is returned **verbatim**, exactly as before.
 *
 * ## Why the direction reversed
 *
 * This function used to keep the LAST `maxIntegerDigits` digits and drop the
 * leading (highest-denomination) ones, retaining the fraction untouched. The PO
 * reversed it: a preparer pasting a too-long figure reads it left-to-right, and
 * losing the leading digits silently changes the value's order of magnitude —
 * in the worst case annihilating it outright (`1000000000000000` capped at 15
 * used to yield `0`). Left-anchoring cannot do that.
 *
 * ## Accepted cost — a pasted fraction is not privileged
 *
 * Because the stream is uniform, `123456789012345.67` on a 13+2 field yields
 * `1234567890123.45`: the cents the author actually wrote are discarded and
 * positional digits take their place. That is deliberate — it keeps ONE rule
 * regardless of whether the pasted text carried a decimal point — and is
 * accepted until the PO raises a follow-up. Note this contradicts the earlier
 * (2026-08-11) "the cents are never what pushed the value over" reasoning,
 * which belonged to the right-anchored rule and no longer applies.
 *
 * ## Leading zeros are measured out before the overflow test
 *
 * Under the old right-anchored rule leading zeros were harmless: the slice took
 * the tail and stripped them afterwards. Keeping the LEFT end would instead
 * preserve `000…` and throw away real digits, so `00012345678901234` has to be
 * measured as 14 digits, not 17. (`clampDecimals` already strips them upstream
 * in `sanitizeToPolicy`; this guard makes the function correct when called
 * directly, which its specs do.)
 *
 * Truncates, never rounds.
 */
export function capIntegerDigits(input: string, maxIntegerDigits: number, decimals: number): string {
  const isNegative = input.startsWith('-');
  const [rawIntPart, fracPart = ''] = input.replace(/-/g, '').split('.');
  const intPart = rawIntPart.replace(/^0+(?=\d)/, '');

  if (intPart.length <= maxIntegerDigits)
    return input;

  const stream = intPart + fracPart;
  const keptInt = stream.slice(0, maxIntegerDigits);
  const keptFrac = stream.slice(maxIntegerDigits, maxIntegerDigits + decimals);
  const rebuilt = keptFrac ? `${keptInt}.${keptFrac}` : keptInt;

  return (isNegative ? '-' : '') + rebuilt;
}

/** Drop the fractional part and any separators. Truncates, never rounds. */
export function coerceInteger(input: string): string {
  const expanded = expandExponential(input);
  const isNegative = expanded.startsWith('-');
  const whole = expanded.split('.')[0].replace(/[^0-9]/g, '');
  return (isNegative && whole ? '-' : '') + whole;
}

/** Remove every minus sign. */
export function stripSign(input: string): string {
  return input.replace(/-/g, '');
}

/**
 * Apply a resolved policy to a raw string value, in the canonical order.
 *
 * Encodes the PO ruling of 2026-08-11 for values arriving from the API
 * (LLM-extracted on first pass, or prior user input on re-submission) into a
 * field that should hold a positive and/or whole number:
 *   - negative  -> take the absolute value
 *   - decimal   -> TRUNCATE, never round
 *   - too long -> keep the digits read LEFT-TO-RIGHT and drop the right-most
 *     ones, re-seating the decimal point after `maxIntegerDigits` digits
 *     (PO, 2026-08-17 — this reversed the original direction); see
 *     `capIntegerDigits`.
 *
 * The direction rule is deliberately the SAME on every path — paste, API
 * hydration and programmatic writes all arrive here — so there is one answer
 * to "which digits survive?" and it cannot drift per entry point. Typing at
 * the cap is the one thing that is not truncation at all: see
 * `SchemaNumericDirective`'s "Overtype at the cap".
 *
 * `coerceInteger` and `clampDecimals` both truncate for this reason. Do not
 * "improve" either to `Math.round`/`Math.floor`: `Math.floor` moves AWAY from
 * zero for negatives (`Math.floor(-1000.9) === -1001`), which would violate the
 * rule. Cover: the `sanitizeToPolicy — PO rule for API-extracted values` specs.
 */
export function sanitizeToPolicy(raw: string, policy: NumericFieldPolicy): string {
  let value = raw ?? '';

  value = policy.integerOnly ? coerceInteger(value) : clampDecimals(value, policy.decimals);

  if (policy.min !== undefined && policy.min >= 0)
    value = stripSign(value);

  value = capIntegerDigits(value, policy.maxIntegerDigits, policy.decimals);

  return value;
}
